/**
 * Creates a sortable image grid with children added to the end of the created grid.
 *
 * Example:
 * // images = [{ id: 'tempId', imageId: 'realIdFromAPI', file: File }];
 * <AddImages images={images}>
 *   <input type="file" accept="images/*" onChange={handleChange} />
 * </AddImages>
 */
import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { ImageFromFile, ResponsiveImage, IconSpinner } from '../../components';
import { FormattedMessage } from '../../util/reactIntl';

import { FiEdit2 } from 'react-icons/fi';

import css from './AddPortfolioImages.css';

const ThumbnailWrapper = props => {
  const {
    className,
    image,
    savedImageAltText,
    onRemoveImage,
    imageUploadInProgress,
    additionalContent,
    showDeleteButton,
    index,
    showCoverBadge,
    deleteIcon,
    deleteLabel,
    savedImageVariants,
  } = props;
  const handleRemoveClick = e => {
    e.stopPropagation();
    onRemoveImage(image.id);
  };

  // Use useEffect to handle the upload progress state change
  useEffect(() => {
    if (image.file) {
      if (!image.imageId) {
        imageUploadInProgress(true);
      } else {
        imageUploadInProgress(false);
      }
    }
  }, [image.file, image.imageId, imageUploadInProgress]);

  const getImageVariants = () => {
    if (savedImageVariants) {
      return savedImageVariants;
    }
    const currentAspect = image.aspectRatio || 1.77777;
    const aspectRatio = currentAspect > 1 ? 'landscape' : currentAspect < 1 ? 'portrait' : 'square';
    const aspectVariants = {
      landscape: ['horizontal', 'horizontal-2x'],
      portrait: ['vertical', 'vertical-2x'],
      square: ['square', 'square2x'],
    };
    return aspectVariants[aspectRatio] || aspectVariants.landscape;
  };
  // Cover image badge - only show for first image (index 0) and when showCoverBadge is true
  const coverBadge =
    index === 0 && showCoverBadge ? (
      <div className={css.coverBadge}>
        <FormattedMessage id="EditPortfolioWorkForm.coverImageLabel" />
      </div>
    ) : null;

  if (image.file) {
    // Add remove button only when the image has been uploaded and can be removed
    const removeButton =
      image.imageId && showDeleteButton ? (
        <button
          type="button"
          className={css.deleteButton}
          onClick={handleRemoveClick}
          aria-label={deleteLabel}
          title={deleteLabel}
        >
          {deleteIcon}
        </button>
      ) : null;

    // While image is uploading we show overlay on top of thumbnail
    const uploadingOverlay = !image.imageId ? (
      <div className={css.thumbnailLoading}>
        <IconSpinner />
      </div>
    ) : null;

    return (
      <ImageFromFile
        id={image.id}
        className={className}
        rootClassName={css.thumbnail}
        file={image.file}
        aspect={image.aspect || 16 / 9}
      >
        {coverBadge}
        {removeButton}
        {uploadingOverlay}
        {additionalContent}
      </ImageFromFile>
    );
  } else {
    const classes = classNames(css.thumbnail, className);
    // Detect portrait orientation for saved images
    const imageAspect =
      image.aspect ||
      image.aspectRatio ||
      (image.attributes?.variants
        ? image.attributes.variants.horizontal?.width /
            image.attributes.variants.horizontal?.height || 1.77777
        : 1.77777);
    const isPortrait = imageAspect < 1;

    // Check if rounded corners are enabled by looking for 'roundedThumbnail' in className
    const hasRoundedCorners = className && className.includes('roundedThumbnail');

    return (
      <div className={classes}>
        {coverBadge}
        {showDeleteButton && (
          <button
            type="button"
            className={css.deleteButton}
            onClick={handleRemoveClick}
            aria-label={deleteLabel}
            title={deleteLabel}
          >
            {deleteIcon}
          </button>
        )}
        <ResponsiveImage
          rootClassName={classNames(
            css.rootForImage,
            isPortrait ? css.portraitImage : null,
            hasRoundedCorners && isPortrait ? css.portraitImageRounded : null
          )}
          image={image}
          alt={savedImageAltText}
          variants={getImageVariants()}
        />
        {additionalContent}
      </div>
    );
  }
};

ThumbnailWrapper.defaultProps = { className: null };

const { array, func, node, string, object } = PropTypes;

ThumbnailWrapper.propTypes = {
  className: string,
  image: object.isRequired,
  savedImageAltText: string.isRequired,
  onRemoveImage: func.isRequired,
};

const AddPortfolioImages = props => {
  const {
    children,
    className,
    thumbnailClassName,
    images,
    savedImageAltText,
    onRemoveImage,
    imageUploadInProgress,
    renderAdditionalContent,
    showDeleteButton,
    showCoverBadge,
    deleteIcon,
    deleteLabel,
    savedImageVariants,
  } = props;
  const classes = classNames(css.root, className);
  return (
    <div className={classes}>
      {images.map((image, index) => {
        return (
          <ThumbnailWrapper
            image={image}
            index={index}
            key={image.id.uuid || image.id}
            className={thumbnailClassName}
            savedImageAltText={savedImageAltText}
            onRemoveImage={onRemoveImage}
            imageUploadInProgress={imageUploadInProgress}
            showDeleteButton={showDeleteButton}
            showCoverBadge={showCoverBadge}
            deleteIcon={deleteIcon}
            deleteLabel={deleteLabel}
            savedImageVariants={savedImageVariants}
            additionalContent={
              renderAdditionalContent && index === 0 ? renderAdditionalContent(image, index) : null
            }
          />
        );
      })}
      {children}
    </div>
  );
};

AddPortfolioImages.defaultProps = {
  className: null,
  thumbnailClassName: null,
  images: [],
  showDeleteButton: true,
  showCoverBadge: false,
  deleteIcon: <FiEdit2 />,
  deleteLabel: undefined,
  savedImageVariants: null,
};

AddPortfolioImages.propTypes = {
  images: array,
  children: node.isRequired,
  className: string,
  thumbnailClassName: string,
  savedImageAltText: string.isRequired,
  onRemoveImage: func.isRequired,
  showCoverBadge: PropTypes.bool,
  // The delete button's icon and accessible name
  deleteIcon: node,
  deleteLabel: string,
  // Image variant names for saved images; by default they are picked by the
  // image's aspect ratio (horizontal, vertical or square)
  savedImageVariants: array,
};

export default AddPortfolioImages;
